import React, { useState, useEffect, useRef } from "react";
import { isPwa } from "../serviceWorkerRegistration";
import { useIsBottomSheetOpen } from "../stores/overlayStore";

import {
  MdIosShare,
  MdOutlineAddBox,
  MdMoreVert,
  MdAdd,
  MdHome,
  MdInstallMobile,
} from "react-icons/md";
import { SiApple } from "react-icons/si";
import { FaChrome } from "react-icons/fa";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

const PwaInstallPrompt: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isPwaInstalled, setIsPwaInstalled] = useState(false);
  const promptShownRef = useRef(false);
  const [showAutoInstall, setShowAutoInstall] = useState(false);
  // A bottom sheet (e.g. the session sheet) is open: stay out of its way.
  const bottomSheetOpen = useIsBottomSheetOpen();

  useEffect(() => {
    // Check if the app is already installed as a PWA
    setIsPwaInstalled(isPwa());

    // Check if user is on a mobile device
    const checkMobile = () => {
      const userAgent =
        navigator.userAgent || navigator.vendor || (window as any).opera;
      return /android|iPad|iPhone|iPod/i.test(userAgent);
    };

    // Check if user is on iOS Safari
    const checkIOSSafari = () => {
      const userAgent =
        navigator.userAgent || navigator.vendor || (window as any).opera;
      const isIOS = /iPad|iPhone|iPod/.test(userAgent);
      const isSafari =
        /Safari/.test(userAgent) && !/Chrome|CriOS|FxiOS/.test(userAgent);
      return isIOS && isSafari;
    };

    // Check if user is on Android
    const checkAndroid = () => {
      const userAgent =
        navigator.userAgent || navigator.vendor || (window as any).opera;
      return /Android/.test(userAgent);
    };

    const mobile = checkMobile();
    const iosSafari = checkIOSSafari();
    const android = checkAndroid();

    setIsMobile(mobile);
    setIsIOS(iosSafari);
    setIsAndroid(android);

    // Don't show anything if already installed as PWA
    if (isPwa()) {
      return;
    }

    // For iOS Safari users, show install instructions after a short delay
    if (iosSafari) {
      const timer = setTimeout(() => {
        setOpen(true);
      }, 2000); // Show after 2 seconds

      return () => clearTimeout(timer);
    }

    // For Android and other mobile devices, listen for beforeinstallprompt
    if (mobile && !iosSafari) {
      const handleBeforeInstallPrompt = (e: BeforeInstallPromptEvent) => {
        // Prevent Chrome 76+ from automatically showing the prompt
        e.preventDefault();
        // Stash the event so it can be triggered later
        setDeferredPrompt(e);
        // Show our custom install prompt
        setOpen(true);
        promptShownRef.current = true;
      };

      window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

      // For Android devices, also show instructions after a delay if beforeinstallprompt doesn't fire
      if (android) {
        const timer = setTimeout(() => {
          // Only show if we haven't already shown due to beforeinstallprompt
          if (!promptShownRef.current) {
            setOpen(true);
          }
        }, 3000); // Show after 3 seconds

        return () => {
          clearTimeout(timer);
          window.removeEventListener(
            "beforeinstallprompt",
            handleBeforeInstallPrompt,
          );
        };
      }

      // Clean up event listener for non-Android mobile devices
      return () => {
        window.removeEventListener(
          "beforeinstallprompt",
          handleBeforeInstallPrompt,
        );
      };
    }
  }, []);

  // Auto-trigger install dialog for Android users after showing instructions
  useEffect(() => {
    if (open && isAndroid && !isIOS && deferredPrompt && !showAutoInstall) {
      // Wait 1 second after showing the prompt, then auto-trigger install
      const timer = setTimeout(() => {
        setShowAutoInstall(true);
        handleInstall();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [open, isAndroid, isIOS, deferredPrompt, showAutoInstall]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleInstall = async () => {
    // For iOS Safari, just close the dialog as instructions are already shown
    if (isIOS) {
      setOpen(false);
      return;
    }

    if (!deferredPrompt) {
      // The deferred prompt isn't available
      setOpen(false);
      return;
    }

    // Show the browser install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const choiceResult = await deferredPrompt.userChoice;

    // User accepted the install prompt
    if (choiceResult.outcome === "accepted") {
      console.log("User accepted the install prompt");
    } else {
      console.log("User dismissed the install prompt");
    }

    // We no longer need the prompt. Clear it up
    setDeferredPrompt(null);
    setOpen(false);
  };

  // If not mobile or already installed as PWA, don't show anything
  if (!isMobile || isPwaInstalled || bottomSheetOpen) {
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      {open && (
        <div
          style={{
            position: "relative",
            zIndex: 9999999,
            height: "100vh",
          }}
          className="fixed inset-0 bg-base-100 bg-opacity-90 backdrop-blur-md z-50 flex items-center justify-center p-4"
        >
          {/* Modal */}
          <div className="max-w-sm w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="p-6 pb-0 text-3xl font-bold">
              <h3 className="font-groteskLogo">
                PEAK<span className="text-base-content/60">^</span>
              </h3>
            </div>

            {/* Content */}
            <div className="p-6 pt-0">
              {isIOS && (
                <div className="mt-4">
                  <div className="flex mb-4">
                    <SiApple className="text-xl mr-3" />
                    <h3 className="text-lg font-semibold text-base-content">
                      Installation Instructions
                    </h3>
                  </div>
                  <div className="space-y-5">
                    <div className="flex items-start">
                      <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-base-200 rounded-md">
                        <MdIosShare className="text-lg w-6 h-6 text-base-content" />
                      </div>
                      <div className="ml-3">
                        <div className="text-sm  text-base-content">
                          1. Tap the Share button
                        </div>
                        <div className="text-xs text-base-content/60">
                          Look for the share icon at the bottom of screen
                        </div>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-base-200 rounded-md">
                        <MdOutlineAddBox className="text-lg w-6 h-6 text-base-content" />
                      </div>
                      <div className="ml-3">
                        <div className="text-sm  text-base-content">
                          2. Select{" "}
                          <span className="font-bold">Add to Home Screen</span>
                        </div>
                        <div className="text-xs text-base-content/60">
                          Scroll down in the share menu to find this option
                        </div>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-base-200 rounded-md">
                        <span className="text-sm font-semibold text-primary">
                          Add
                        </span>
                      </div>
                      <div className="ml-3">
                        <div className="text-sm  text-base-content">
                          3. Tap <span className="font-bold">Add</span> to
                          confirm
                        </div>
                        <div className="text-xs text-base-content/60">
                          The app will appear on your home screen
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Android/Chrome Instructions */}
              {isAndroid && !isIOS && (
                <div className="mt-4">
                  <div className="flex mb-4">
                    <FaChrome className="text-xl mr-3 text-blue-600" />
                    <h3 className="text-lg font-semibold text-base-content/80">
                      Android Installation Instructions
                    </h3>
                  </div>
                  <div className="space-y-5">
                    <div className="flex items-start">
                      <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-base-200 rounded-md">
                        <MdMoreVert className="text-lg w-6 h-6 text-base-content" />
                      </div>
                      <div className="ml-3">
                        <div className="text-sm  text-base-content">
                          1. Tap the menu button (⋮)
                        </div>
                        <div className="text-xs text-base-content/60">
                          Look for the three dots in the top right corner of
                          Chrome
                        </div>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-base-200 rounded-md">
                        <MdInstallMobile className="text-lg w-6 h-6 text-base-content" />
                      </div>
                      <div className="ml-3">
                        <div className="text-sm  text-base-content">
                          2. Select{" "}
                          <span className="font-bold">Add to Home screen</span>
                        </div>
                        <div className="text-xs text-base-content/60">
                          Or "Install app" option in the menu
                        </div>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-base-200 rounded-md">
                        <span className="text-sm font-semibold text-primary">
                          ✓
                        </span>
                      </div>
                      <div className="ml-3">
                        <div className="text-sm  text-base-content">
                          3. Tap <span className="font-bold">Install</span> to
                          confirm
                        </div>
                        <div className="text-xs text-base-content/60">
                          The app will appear on your home screen
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="px-6 py-4 flex justify-end space-x-3">
              <button
                onClick={handleClose}
                className="font-heading w-full border bg-base-200 border-base-300 rounded-xl p-4 py-2  text-base-content font-bold"
              >
                {isIOS ? "Continue" : "Continue in Browser"}
              </button>
              {!isIOS && deferredPrompt && (
                <button
                  onClick={handleInstall}
                  className="btn btn-primary"
                  autoFocus
                >
                  Install App
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PwaInstallPrompt;
